import { isAccentTextFillAnimation } from "@homeinstead/schemas";
import {
  ACCENT_TEXT_FILL_DURATION_MILLISECONDS,
  ACCENT_TEXT_FILL_STAGGER_MILLISECONDS,
  ACCENT_TEXT_FILL_START_MILLISECONDS,
} from "./accent-text-fill-timing";

type RichTextSpan = {
  _type: string;
  marks?: string[];
  text?: string;
};

type RichTextBlock = {
  _type: string;
  children?: RichTextSpan[];
  markDefs?: { _key: string; _type: string; animation?: string }[];
  style?: string;
};

const HEADING_STYLES = new Set(["h1", "h2", "h3", "h4", "h5", "h6"]);

function isHeadingBlock(value: unknown): value is RichTextBlock {
  return (
    typeof value === "object" &&
    value !== null &&
    "_type" in value &&
    value._type === "block" &&
    "style" in value &&
    typeof value.style === "string" &&
    HEADING_STYLES.has(value.style)
  );
}

/**
 * Only a span whose single mark is the accent letter-animates; other marks
 * nest another element, and `AccentText` leaves those solid.
 */
function getAccentTextAnimationSpanLength(
  child: RichTextSpan,
  accentKeys: Set<string>,
) {
  if (child._type !== "span" || typeof child.text !== "string") {
    return 0;
  }

  const marks = child.marks ?? [];
  const [onlyMark] = marks;
  if (marks.length !== 1 || !onlyMark || !accentKeys.has(onlyMark)) {
    return 0;
  }

  return Array.from(child.text).length;
}

function getLongestAccentTextAnimationSpan(text: unknown) {
  if (!Array.isArray(text)) {
    return 0;
  }

  let longest = 0;

  for (const block of text) {
    if (!isHeadingBlock(block)) {
      continue;
    }

    const accentKeys = new Set(
      (block.markDefs ?? [])
        .filter(
          (mark) =>
            mark._type === "accentText" &&
            isAccentTextFillAnimation(mark.animation),
        )
        .map((mark) => mark._key),
    );

    if (accentKeys.size === 0) {
      continue;
    }

    for (const child of block.children ?? []) {
      longest = Math.max(
        longest,
        getAccentTextAnimationSpanLength(child, accentKeys),
      );
    }
  }

  return longest;
}

/**
 * When the accent-fill headline animation in this Portable Text finishes, in
 * milliseconds after load. Zero when nothing in it animates. Each span is its
 * own `AccentText` whose letter indexes restart, so the spans run together
 * and the end follows the longest one. Accent text outside h1–h6 does not
 * animate.
 */
function getAccentTextFillEndMilliseconds(text: unknown): number {
  const characters = getLongestAccentTextAnimationSpan(text);
  if (characters === 0) {
    return 0;
  }

  return (
    ACCENT_TEXT_FILL_START_MILLISECONDS +
    (characters - 1) * ACCENT_TEXT_FILL_STAGGER_MILLISECONDS +
    ACCENT_TEXT_FILL_DURATION_MILLISECONDS
  );
}

export { getAccentTextFillEndMilliseconds };
